/**
 * Pizza Detail Overlay (sections/tea-pizza-detail-overlay.liquid)
 *
 * Makes the Build-Your-Own-Box flavor tiles tap-to-open a popup with photos and copy.
 *
 * Bundle logic is never touched. The popup's stepper presses the tile's real minus / plus buttons and reads the
 * real input's value back, so limits, pricing and the "x of y selected" count stay owned by the bundle script.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 959px)');
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var TILE_SELECTOR = '.pizza-variant-option-container';
  var QTY_INPUT_SELECTOR = '[js-flex-bundles-quantity-input]';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function fill(template, map) {
    return String(template || '').replace(/%(\w+)%/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : m;
    });
  }

  function Overlay(root) {
    var self = this;
    this.root = root;
    this.S = JSON.parse(root.querySelector('[data-tpo-settings]').textContent);
    this.pizzas = {};
    this.byBlock = {};
    this.tiles = {};
    this.current = null;
    this.lastFocus = null;
    this.viewedTimer = null;

    root.querySelectorAll('[data-tpo-pizza]').forEach(function (el) {
      var p = JSON.parse(el.textContent);
      p.id = String(p.id);
      self.pizzas[p.id] = p;
      if (p.blockId) self.byBlock[p.blockId] = p.id;
    });

    var q = function (sel) { return root.querySelector(sel); };
    this.el = {
      veil: q('[data-tpo-veil]'),
      sheet: q('[data-tpo-sheet]'),
      grab: q('[data-tpo-grab]'),
      close: q('[data-tpo-close]'),
      body: q('[data-tpo-body]'),
      gallery: q('[data-tpo-gallery]'),
      track: q('[data-tpo-track]'),
      dots: q('[data-tpo-dots]'),
      prev: q('[data-tpo-prev]'),
      next: q('[data-tpo-next]'),
      chip: q('[data-tpo-chip]'),
      title: q('[data-tpo-title]'),
      desc: q('[data-tpo-desc]'),
      stepper: q('[data-tpo-stepper]'),
      stepperLabel: q('[data-tpo-stepper-label]'),
      qty: q('[data-tpo-qty]'),
      minus: q('[data-tpo-minus]'),
      plus: q('[data-tpo-plus]'),
      note: q('[data-tpo-note]'),
      count: q('[data-tpo-count]'),
      done: q('[data-tpo-done]')
    };

    if (this.S.accent) document.documentElement.style.setProperty('--tpo-accent', this.S.accent);

    this.bindTiles();
    if (!Object.keys(this.tiles).length) return;

    this.bindSheet();
    this.bindGallery();
    this.bindDrag();
    this.preloadFirstPhotos();
  }

  /**
   * After the page has finished loading, quietly warm the browser cache with each pizza's first photo, one at a
   * time and at low priority, so the popup opens with its hero photo already there. Remaining photos still load
   * lazily once the popup is open.
   */
  Overlay.prototype.preloadFirstPhotos = function () {
    var self = this;
    var queue = Object.keys(this.tiles).map(function (id) {
      var first = (self.pizzas[id].images || [])[0];
      return typeof first === 'string' ? first : (first && first.src);
    }).filter(Boolean);
    if (!queue.length) return;

    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 800); };
    var next = function () {
      var src = queue.shift();
      if (!src) return;
      var img = new Image();
      img.decoding = 'async';
      try { img.fetchPriority = 'low'; } catch (err) { /* older browsers */ }
      img.onload = img.onerror = function () { idle(next); };
      img.src = src;
    };
    var start = function () { idle(next); };

    if (document.readyState === 'complete') {
      setTimeout(start, 500);
    } else {
      window.addEventListener('load', function () { setTimeout(start, 500); }, { once: true });
    }
  };

  /* ---------- tiles ---------- */

  Overlay.prototype.tileVariantId = function (tile) {
    var input = tile.querySelector(QTY_INPUT_SELECTOR);
    if (input && input.dataset.variantId) return String(input.dataset.variantId);

    // The "included" flavor tile has no stepper; its variant id sits in a hidden input just before it.
    var node = tile.previousElementSibling;
    while (node) {
      if (node.matches && node.matches('input[name="included_flavor"]')) return String(node.value);
      if (node.matches && node.matches(TILE_SELECTOR)) break;
      node = node.previousElementSibling;
    }
    return null;
  };

  Overlay.prototype.bindTiles = function () {
    var self = this;

    document.querySelectorAll(TILE_SELECTOR).forEach(function (tile) {
      if (tile.closest('[data-tpo]') || tile.dataset.tpoBound) return;
      var id = self.tileVariantId(tile);
      if (!id || !self.pizzas[id]) return;

      tile.dataset.tpoBound = '1';
      tile.classList.add('tpo-tile');
      self.tiles[id] = tile;

      var hit = tile.firstElementChild;
      if (hit && !hit.querySelector('quantity-selector')) {
        hit.classList.add('tpo-tile__hit');
        hit.setAttribute('role', 'button');
        hit.setAttribute('tabindex', '0');
        hit.setAttribute('aria-haspopup', 'dialog');
        hit.setAttribute('aria-label', 'View ' + self.pizzas[id].title);
        hit.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.openedByKeyboard = true;
            self.open(id);
          }
        });
      }

      var isControl = function (target) {
        return !!target.closest('quantity-selector, .product-info__quantity-selector, input, select, textarea, a');
      };

      tile.addEventListener('pointerdown', function (e) {
        if (isControl(e.target)) return;
        tile.classList.add('is-pressed');
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (name) {
        tile.addEventListener(name, function () { tile.classList.remove('is-pressed'); });
      });

      tile.addEventListener('click', function (e) {
        if (isControl(e.target)) return;
        e.preventDefault();
        self.openedByKeyboard = false;
        self.open(id);
      });
    });
  };

  /* ---------- open / close ---------- */

  Overlay.prototype.open = function (id) {
    var p = this.pizzas[id];
    var tile = this.tiles[id];
    if (!p || !tile) return;

    var el = this.el;
    var S = this.S;
    var self = this;

    // Only one tile is ever highlighted: clear open / fading state from every other tile first.
    clearTimeout(this.viewedTimer);
    Object.keys(this.tiles).forEach(function (otherId) {
      if (otherId !== id) self.tiles[otherId].classList.remove('is-open', 'is-viewed');
    });

    this.current = {
      id: id,
      p: p,
      tile: tile,
      input: tile.querySelector(QTY_INPUT_SELECTOR),
      selector: tile.querySelector('quantity-selector')
    };

    this.renderGallery(p.images || []);

    var soldOut = p.available === false;
    var chip = soldOut ? S.chipSoldOut : p.chip;
    el.chip.textContent = chip || '';
    el.chip.hidden = !chip;
    el.chip.classList.toggle('tpo-chip--muted', soldOut);

    el.title.textContent = p.popupTitle || p.title;
    el.desc.innerHTML = p.desc || '';
    el.desc.hidden = !p.desc;

    var useStepper = !soldOut && S.showStepper && this.current.input && this.current.selector;
    el.stepper.hidden = !useStepper;
    el.note.hidden = true;
    if (soldOut) {
      el.note.textContent = S.chipSoldOut || '';
      el.note.hidden = !S.chipSoldOut;
    } else if (!useStepper && p.kind === 'included') {
      el.note.textContent = S.chipIncluded || '';
      el.note.hidden = !S.chipIncluded;
    }
    if (useStepper) this.sync();
    this.updateCount();

    tile.classList.remove('is-viewed');
    tile.classList.add('is-open');
    this.root.classList.add('is-open');
    el.sheet.setAttribute('aria-hidden', 'false');
    el.sheet.style.transform = '';
    document.documentElement.classList.add('tpo-scroll-lock');

    this.lastFocus = document.activeElement;
    el.body.scrollTop = 0;
    this.goTo(0, false);

    // Focus the dialog container (not the X) so a tap never paints a focus ring; Tab then lands on the X first.
    try { el.sheet.focus({ preventScroll: true }); } catch (err) { el.sheet.focus(); }
  };

  Overlay.prototype.close = function () {
    if (!this.current) return;
    var el = this.el;
    var tile = this.current.tile;

    this.root.classList.remove('is-open');
    el.sheet.setAttribute('aria-hidden', 'true');
    el.sheet.style.transform = '';
    el.sheet.style.transition = '';
    document.documentElement.classList.remove('tpo-scroll-lock');

    // Leave a brief, fading trace on the tile so the shopper sees which pizza they just looked at.
    tile.classList.remove('is-open');
    tile.classList.add('is-viewed');
    this.viewedTimer = setTimeout(function () { tile.classList.remove('is-viewed'); }, 1600);

    // Return focus to the tile only for keyboard users; after a tap or click, leave nothing focused so no focus
    // ring is painted on the tile.
    var focusTarget = this.openedByKeyboard ? (tile.querySelector('.tpo-tile__hit') || this.lastFocus) : null;
    this.current = null;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      try { focusTarget.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
    } else if (document.activeElement && document.activeElement.blur && el.sheet.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  };

  /* ---------- stepper mirror ---------- */

  Overlay.prototype.realButton = function (which) {
    var selector = this.current && this.current.selector;
    if (!selector) return null;
    return selector.querySelector(which === 'minus' ? 'button:first-of-type' : 'button:last-of-type');
  };

  Overlay.prototype.sync = function () {
    if (!this.current || !this.current.input) return;
    var el = this.el;
    var qty = parseInt(this.current.input.value, 10) || 0;
    el.qty.textContent = String(qty);
    el.stepperLabel.textContent = qty > 0 ? fill(this.S.inBoxLabel, { n: qty }) : (this.S.addLabel || '');
    el.minus.disabled = qty <= 0;
    var realPlus = this.realButton('plus');
    el.plus.disabled = !realPlus || realPlus.disabled || realPlus.getAttribute('aria-disabled') === 'true';
    this.updateCount();
  };

  Overlay.prototype.updateCount = function () {
    var el = this.el;
    if (!this.S.showCount || !this.S.countLabel) { el.count.hidden = true; return; }
    var cur = document.querySelector('[js-pizza-amount-current]');
    var tot = document.querySelector('[js-pizza-amount-total]');
    if (!cur || !tot) { el.count.hidden = true; return; }
    el.count.textContent = fill(this.S.countLabel, { c: cur.textContent.trim(), t: tot.textContent.trim() });
    el.count.hidden = false;
  };

  Overlay.prototype.step = function (which) {
    var button = this.realButton(which);
    if (!button || button.disabled) return;
    button.click();
    var self = this;
    requestAnimationFrame(function () { self.sync(); });
  };

  /* ---------- gallery ---------- */

  Overlay.prototype.renderGallery = function (images) {
    var el = this.el;
    var title = this.current ? this.current.p.title : '';

    // Fixed frame (see CSS): photos fill it edge to edge, anchored top-center, so squares and landscapes share
    // one consistent height and the sheet never resizes between slides.
    el.track.innerHTML = images.map(function (image, i) {
      var src = typeof image === 'string' ? image : image.src;
      var safeTitle = title.replace(/"/g, '&quot;');
      var lazy = i === 0 ? '' : ' loading="lazy"';
      return '<div class="tpo-slide">' +
        '<img class="tpo-slide__img" src="' + src + '" alt="' + safeTitle + '"' + lazy + ' draggable="false">' +
        '</div>';
    }).join('');

    el.dots.innerHTML = images.length > 1 ? images.map(function (_, i) {
      return '<button type="button" class="' + (i === 0 ? 'is-active' : '') + '" aria-label="Photo ' + (i + 1) + '" data-tpo-dot="' + i + '"></button>';
    }).join('') : '';

    el.gallery.hidden = images.length === 0;
    el.gallery.classList.toggle('tpo-gallery--single', images.length < 2);
    el.track.scrollLeft = 0;
  };


  Overlay.prototype.slideIndex = function () {
    var track = this.el.track;
    var width = track.clientWidth || 1;
    return Math.round(track.scrollLeft / width);
  };

  Overlay.prototype.goTo = function (index, smooth) {
    var track = this.el.track;
    var count = track.children.length;
    if (!count) return;
    index = Math.max(0, Math.min(count - 1, index));
    var left = index * track.clientWidth;
    if (smooth !== false && !REDUCED_MOTION.matches) {
      track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      track.scrollLeft = left;
    }
    this.setActiveDot(index);
  };

  Overlay.prototype.setActiveDot = function (index) {
    Array.prototype.forEach.call(this.el.dots.children, function (dot, i) {
      dot.classList.toggle('is-active', i === index);
    });
  };

  Overlay.prototype.bindGallery = function () {
    var self = this;
    var el = this.el;
    el.track.addEventListener('scroll', function () {
      self.setActiveDot(self.slideIndex());
    }, { passive: true });

    el.dots.addEventListener('click', function (e) {
      var dot = e.target.closest('[data-tpo-dot]');
      if (dot) self.goTo(parseInt(dot.dataset.tpoDot, 10));
    });

    el.prev.addEventListener('click', function () { self.goTo(self.slideIndex() - 1); });
    el.next.addEventListener('click', function () { self.goTo(self.slideIndex() + 1); });
  };

  /* ---------- sheet wiring ---------- */

  Overlay.prototype.bindSheet = function () {
    var self = this;
    var el = this.el;

    el.close.addEventListener('click', function () { self.close(); });
    el.done.addEventListener('click', function () { self.close(); });
    el.veil.addEventListener('click', function () { self.close(); });
    el.minus.addEventListener('click', function () { self.step('minus'); });
    el.plus.addEventListener('click', function () { self.step('plus'); });

    // Keep the mirrored stepper honest if the real input changes while the popup is open.
    ['input', 'change'].forEach(function (name) {
      document.addEventListener(name, function (e) {
        if (!self.current) return;
        if (e.target === self.current.input || (e.target.matches && e.target.matches(QTY_INPUT_SELECTOR + ', [js-pack-size-input]'))) {
          self.sync();
        }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (!self.current) return;
      if (e.key === 'Escape') { e.preventDefault(); self.close(); return; }
      if (e.key === 'ArrowLeft') { self.goTo(self.slideIndex() - 1); return; }
      if (e.key === 'ArrowRight') { self.goTo(self.slideIndex() + 1); return; }
      if (e.key === 'Tab') {
        var focusable = Array.prototype.filter.call(el.sheet.querySelectorAll(FOCUSABLE), function (n) {
          return n.offsetParent !== null;
        });
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    // Theme editor: selecting a Pizza block previews that pizza's popup.
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', function (e) {
        var anchor = e.target && e.target.closest ? e.target.closest('[data-tpo-block]') : null;
        if (!anchor || !self.root.contains(anchor)) return;
        var id = self.byBlock[anchor.dataset.tpoBlock];
        if (id) self.open(id);
      });
      document.addEventListener('shopify:block:deselect', function (e) {
        var anchor = e.target && e.target.closest ? e.target.closest('[data-tpo-block]') : null;
        if (anchor && self.root.contains(anchor)) self.close();
      });
    }
  };

  /* ---------- swipe down to dismiss (mobile) ---------- */

  Overlay.prototype.bindDrag = function () {
    var self = this;
    var el = this.el;
    var drag = null;

    el.sheet.addEventListener('touchstart', function (e) {
      if (!MOBILE.matches || !self.current || e.touches.length !== 1) return;
      var t = e.touches[0];
      drag = {
        x: t.clientX,
        y: t.clientY,
        t: Date.now(),
        active: false,
        cur: 0,
        fromGrab: !!e.target.closest('[data-tpo-grab]'),
        inTrack: !!e.target.closest('[data-tpo-track]')
      };
    }, { passive: true });

    el.sheet.addEventListener('touchmove', function (e) {
      if (!drag || e.touches.length !== 1) return;
      var t = e.touches[0];
      var dx = t.clientX - drag.x;
      var dy = t.clientY - drag.y;

      if (!drag.active) {
        if (Math.abs(dy) < 8 && Math.abs(dx) < 8) return;
        var vertical = Math.abs(dy) > Math.abs(dx) * 1.2;
        var canDrag = drag.fromGrab || (vertical && dy > 0 && el.body.scrollTop <= 0);
        if (!canDrag) { drag = null; return; }
        drag.active = true;
        el.sheet.style.transition = 'none';
      }

      drag.cur = Math.max(0, dy);
      el.sheet.style.transform = 'translateY(' + drag.cur + 'px)';
      if (e.cancelable) e.preventDefault();
    }, { passive: false });

    var end = function () {
      if (!drag) return;
      var d = drag;
      drag = null;
      if (!d.active) return;
      el.sheet.style.transition = '';
      var flick = d.cur > 40 && (Date.now() - d.t) < 250;
      if (d.cur > 110 || flick) {
        self.close();
      } else {
        el.sheet.style.transform = '';
      }
    };
    el.sheet.addEventListener('touchend', end);
    el.sheet.addEventListener('touchcancel', end);
  };

  /* ---------- init ---------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-tpo]').forEach(function (root) {
      if (root.tpoOverlay) return;
      root.tpoOverlay = new Overlay(root);
    });
  }

  function ready() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
    } else {
      init();
    }
  }

  ready();

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-tpo]') : null;
    if (root && root.tpoOverlay && root.tpoOverlay.current) root.tpoOverlay.close();
    document.documentElement.classList.remove('tpo-scroll-lock');
  });
})();
